/**
 * BookingSummary — running summary panel rendered alongside the
 * unified /book flow. Reflects whatever the customer has picked so
 * far so they can keep their place across the multi-step funnel.
 *
 * Desktop: sticky right rail, always visible.
 * Mobile: collapsible "Your booking" card at the top of the page,
 *   chevron toggles open/closed (closed by default to save vertical
 *   space, opens automatically once a wig has services picked).
 *
 * Pure render — no fetches, no state mutations of upstream values.
 * Parent passes the live props on every render.
 */
"use client";

import { useState } from "react";
import {
  Calendar,
  ChevronDown,
  ChevronUp,
  Image as ImageIcon,
  Package,
  Sparkles,
  Truck,
} from "lucide-react";
import { useTranslations } from "next-intl";
import { SERVICES, type WigLine } from "@/lib/booking-services";

interface BookingSummaryProps {
  wigs: WigLine[];
  wantsNewWigConsult: boolean;
  /** Picked appointment date "YYYY-MM-DD". Empty when not yet chosen. */
  date: string;
  /** Picked appointment time string ("10:30 AM"). */
  time: string;
  /** Drop-off date "YYYY-MM-DD". */
  dropDate: string;
  wantsCourier: boolean;
  /** Deposit amount owed for the booking, dollars. 0 = none. */
  depositAmount: number | null;
}

function serviceLabel(id: string, tSvc: (key: string) => string): string {
  // Pull translated label via the service.<id>_label key the cron
  // populates per locale. Falls back to the English SERVICES.label
  // when the key is missing (graceful for new services not yet seeded).
  try {
    return tSvc(`${id}_label`);
  } catch {
    return SERVICES.find((s) => s.id === id)?.label ?? id;
  }
}

function formatDateShort(dateStr: string): string {
  if (!dateStr) return "";
  return new Date(dateStr + "T12:00:00").toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
}

export default function BookingSummary({
  wigs,
  wantsNewWigConsult,
  date,
  time,
  dropDate,
  wantsCourier,
  depositAmount,
}: BookingSummaryProps) {
  const t = useTranslations("booking");
  const tSvc = useTranslations("service");
  const [mobileOpen, setMobileOpen] = useState(false);
  const wigsWithServices = wigs.filter((w) => w.selectedServices.length > 0);
  const hasAnythingPicked = wantsNewWigConsult || wigsWithServices.length > 0;
  const itemCount = wigsWithServices.length + (wantsNewWigConsult ? 1 : 0);

  // ─── shared body ──────────────────────────────────────────────────
  const body = (
    <div className="space-y-3 text-sm">
      {!hasAnythingPicked ? (
        <p className="text-xs text-brand-muted leading-relaxed">
          {t("summaryEmptyHint")}
        </p>
      ) : (
        <>
          {wantsNewWigConsult && (
            <div className="flex items-start gap-2">
              <Sparkles className="h-4 w-4 text-brand-gold flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="text-brand-dark font-medium">{t("summaryNewWigConsult")}</p>
                <p className="text-[11px] text-brand-muted">{t("summaryNewWigDeposit")}</p>
              </div>
            </div>
          )}

          {wigsWithServices.map((w, i) => (
            <div key={w.key} className="flex items-start gap-2">
              {w.location === "dropoff" ? (
                <Package className="h-4 w-4 text-brand-gold flex-shrink-0 mt-0.5" />
              ) : (
                <Calendar className="h-4 w-4 text-brand-gold flex-shrink-0 mt-0.5" />
              )}
              <div className="flex-1 min-w-0">
                <p className="text-brand-dark font-medium truncate">
                  {w.wigName || t("summaryWigDefault", { index: i + 1 })}
                </p>
                <p className="text-[11px] text-brand-muted">
                  {w.selectedServices.map((id) => serviceLabel(id, tSvc)).join(" · ")}
                </p>
                <p className="text-[11px] text-brand-muted">
                  {w.location === "dropoff"
                    ? t("summaryDroppingOff")
                    : w.location === "appointment"
                      ? t("summaryInSalon")
                      : t("summaryPickLocation")}
                  {w.photos.length > 0 && (
                    <span className="ms-1.5 inline-flex items-center gap-0.5">
                      <ImageIcon className="h-3 w-3" />
                      {w.photos.length}
                    </span>
                  )}
                </p>
              </div>
            </div>
          ))}

          {(date || time) && (
            <div className="flex items-start gap-2 pt-2 border-t border-gray-100">
              <Calendar className="h-4 w-4 text-brand-gold flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-[11px] uppercase tracking-wider text-brand-muted">
                  {t("summaryAppointmentLabel")}
                </p>
                <p className="text-brand-dark text-xs">
                  {date ? formatDateShort(date) : t("summaryDateFallback")}
                  {time ? ` · ${time}` : ""}
                </p>
              </div>
            </div>
          )}

          {dropDate && (
            <div className="flex items-start gap-2 pt-2 border-t border-gray-100">
              <Package className="h-4 w-4 text-brand-gold flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-[11px] uppercase tracking-wider text-brand-muted">
                  {t("summaryDropoffLabel")}
                </p>
                <p className="text-brand-dark text-xs">
                  {formatDateShort(dropDate)}
                  {wantsCourier && (
                    <span className="ms-1.5 inline-flex items-center gap-1 text-[11px] text-brand-muted">
                      <Truck className="h-3 w-3" />
                      {t("summaryUberCourier")}
                    </span>
                  )}
                </p>
              </div>
            </div>
          )}

          {depositAmount && depositAmount > 0 ? (
            <div className="pt-2 border-t border-gray-100">
              <div className="flex items-center justify-between text-sm">
                <span className="text-brand-muted">{t("summaryDepositDue")}</span>
                <span className="font-medium text-brand-dark">
                  ${depositAmount.toFixed(2)}
                </span>
              </div>
              <p className="text-[10px] text-brand-muted mt-1 leading-relaxed">
                {t("summaryDepositCaveat")}
              </p>
            </div>
          ) : null}
        </>
      )}
    </div>
  );

  return (
    <>
      {/* Mobile — collapsible at the top */}
      <div className="md:hidden mb-6">
        <button
          type="button"
          onClick={() => setMobileOpen((v) => !v)}
          className="w-full flex items-center justify-between gap-2 rounded-xl border border-gray-200 bg-white px-4 py-3 text-left shadow-sm"
          aria-expanded={mobileOpen}
        >
          <div>
            <p className="text-[10px] uppercase tracking-[0.2em] text-brand-gold font-medium">
              {t("summaryYourBooking")}
            </p>
            <p className="text-xs text-brand-muted">
              {hasAnythingPicked
                ? t("summaryItemCount", { count: itemCount })
                : t("summaryTapToView")}
            </p>
          </div>
          {mobileOpen ? (
            <ChevronUp className="h-4 w-4 text-brand-muted" />
          ) : (
            <ChevronDown className="h-4 w-4 text-brand-muted" />
          )}
        </button>
        {mobileOpen && (
          <div className="mt-2 rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
            {body}
          </div>
        )}
      </div>

      {/* Desktop — sticky right rail */}
      <aside className="hidden md:block sticky top-6 self-start rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
        <p className="text-[10px] uppercase tracking-[0.2em] text-brand-gold font-medium mb-3">
          {t("summaryYourBooking")}
        </p>
        {body}
      </aside>
    </>
  );
}
